'use client'
import { Box, Slider as MUISlider } from '@mui/material'
import type { SxProps } from '@mui/material'
import type { IColor } from './types'
import { useEffect, useRef } from 'react'

interface ISliderProps {
  value: number
  range: [number, number]
  onChange: (value: number) => void
  sx?: SxProps
  withMarks?: boolean
  color?: string
  debounceMs?: number
}
const DEBOUNCE_MS = 300

export default function Slider({ value, range, onChange, withMarks = true, sx, color = 'secondary' }: ISliderProps) {
  const [min, max] = range
  const timeoutRef = useRef<NodeJS.Timeout>(undefined)

  const debouncedOnChange = (newValue: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      onChange(newValue)
    }, DEBOUNCE_MS)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <MUISlider
        value={value}
        min={min}
        max={max}
        step={1}
        valueLabelDisplay="on"
        onChange={(_, newValue) => {
          if (typeof newValue === 'number') {
            debouncedOnChange(newValue)
          }
        }}
        marks={
          withMarks && [
            { value: min, label: min.toString() },
            { value: max, label: max.toString() },
          ]
        }
        sx={{
          '.MuiSlider-thumb': {
            height: 24,
            width: 24,
            backgroundColor: '#fff',
            border: '1px solid primary.main',
            '&:hover': {
              boxShadow: '0 0 0 8px rgba(58, 133, 137, 0.16)',
            },
          },
          color: color.startsWith('#') ? color : undefined,
          ...sx,
        }}
        color={!color.startsWith('#') ? (color as IColor) : undefined}
      />
    </Box>
  )
}
